import RoleUser from "./RoleUser";
import Role from "./Role";

/**
 * User class representing a user in the system
 */
class User {
  /**
   * The unique identifier of the user
   */
  private readonly id: number | null;

  /**
   * The last name of the user
   */
  private name: string | null;

  /**
   * The first name of the user
   */
  private first_name: string | null;

  /**
   * The email address of the user
   */
  private mail: string | null;

  private system: string | null;

  /**
   * The unique identifier of the user's entity
   */
  private entity_id: number | null;

  /**
   * The date and time at which the user was created
   */
  private readonly created_at: string | null;

  /**
   * The date and time at which the user was last updated
   */
  private readonly updated_at: string | null;

  /**
   * The roles of the user
   */
  private role_user: RoleUser[] | null;


  constructor(
    id: number | null = null,
    name: string | null,
    first_name: string | null,
    mail: string | null,
    entity_id: number | null = null,
    system: string | null = null,
    created_at: string | null = null,
    updated_at: string | null = null,
    role_user: RoleUser[] | null = null
  ) {
    this.id = id;
    this.name = name;
    this.first_name = first_name;
    this.mail = mail;
    this.system = system;
    this.entity_id = entity_id;
    this.created_at = created_at;
    this.updated_at = updated_at;
    this.role_user = role_user;
  }

  /**
   * Converts a JSON object into a User instance.
   *
   * @param {any} item - The JSON object containing user data to be converted.
   * @return {User} A new User instance created from the provided JSON data.
   */
  public static fromJson(item: any): User {
    return new User(
      item.id,
      item.name,
      item.first_name,
      item.mail,
      item.entity_id,
      item.created_at,
      item.updated_at,
      item.role_user.map((role_user: any) => RoleUser.fromJson(role_user))
    );
  }


  getSystem(): string | null {
    return this.system;
  }

  /**
   * Gets the user's unique identifier
   * @returns The user's ID
   */
  getId(): number | null {
    return this.id;
  }

  /**
   * Gets the user's last name
   * @returns The user's last name
   */
  getName(): string | null {
    return this.name;
  }

  /**
   * Gets the user's first name
   * @returns The user's first name
   */
  getFirstName(): string | null {
    return this.first_name;
  }

  /**
   * Combines the first name and last name of a person to return their full name.
   *
   * @return {string} The full name, consisting of the first name and last name separated by a space.
   */
  getFullName(): string | null {
    return this.first_name + " " + this.name;
  }

  /**
   * Gets the user's email address
   * @returns The user's email address
   */
  getMail(): string | null {
    return this.mail;
  }


  /**
   * Gets the user's entity identifier
   * @returns The user's entity ID
   */
  getEntityId(): number | null {
    return this.entity_id;
  }

  /**
   * Gets the date and time at which the user was created
   * @returns The user's creation timestamp
   */
  getCreatedAt(): string | null {
    return this.created_at;
  }

  /**
   * Gets the date and time at which the user was last updated
   * @returns The user's last update timestamp
   */
  getUpdatedAt(): string | null {
    return this.updated_at;
  }

  /**
   * Retrieves the list of role-user associations.
   *
   * @return {RoleUser[]} An array of RoleUser objects representing the associations.
   */
  getRoleUser(): RoleUser[] | null {
    return this.role_user;
  }

  /**
   * Retrieves the roles associated with a business unit (BU).
   *
   * @return {Role[]} An array of roles. Returns an empty array if no roles are found.
   */
  getRoleByBu(buId: number): Role[] {
    if (
      typeof this.role_user !== 'undefined' &&
      this.role_user &&
      this.role_user?.length > 0
    ) {
      return this.role_user
        .filter(roleUser => parseInt(String(roleUser.getEntityId())) === buId)
        .map(roleUser => roleUser.getRole());
    }

    return [];
  }

  /**
   * Checks if the current user has the "Super Administrateur" role.
   *
   * @return {boolean} Returns true if the user has the "Super Administrateur" role, otherwise false.
   */
  isAccountManager(): boolean {
    return this.role_user !== null &&
      this.role_user.some(roleUser => roleUser.getRole().getName() === 'Super Administrateur');
  }

  /**
   * Checks if the user has a specific role based on the provided role ID.
   *
   * @param {number} roleId - The unique identifier of the role to check for.
   * @return {boolean} True if the user has the specified role, otherwise false.
   */
  hasRole(roleId: number): boolean {
    return this.role_user !== null &&
      this.role_user.some(roleUser => roleUser.getRole().getId() === roleId);
  }

  /**
   * Checks if a specific role is associated with the business unit (BU) for the user.
   *
   * @param {number} roleId - The ID of the role to check.
   * @param {number} buId - The ID of the business unit to check.
   * @return {boolean} Returns true if the user has the specified role on the given business unit, otherwise false.
   */
  hasRoleOnBu(roleId: number, buId: number): boolean {
    return this.role_user !== null &&
      this.role_user.some(
        roleUser => parseInt(String(roleUser.getEntityId())) === buId && roleUser.getRole().getId() === roleId
      );
  }

  /**
   * Sets the user's last name
   * @param {string} name - The last name to set
   */
  setName(name: string): void {
    this.name = name;
  }

  /**
   * Sets the user's first name
   * @param {string} first_name - The first name to set
   */
  setFirstName(first_name: string): void {
    this.first_name = first_name;
  }

  /**
   * Sets the user's email address
   * @param {string} mail - The email address to set
   */
  setMail(mail: string): void {
    this.mail = mail;
  }


  addRoleOnBu(roleId: number, buId: number, role: Role, system: string): void {
    if (!this.hasRoleOnBu(roleId, buId)) {
      this.role_user!.push(
        new RoleUser(
          this.getMail()!,
          roleId,
          buId,
          null,
          null,
          null,
          role,
          system
        )
      );
    }
  }

  removeRoleOnBu(roleId: number, buId: number): void {
    if (this.hasRoleOnBu(roleId, buId)) {
      this.role_user!.splice(
        this.role_user!.findIndex(
          roleUser => parseInt(String(roleUser.getEntityId())) === buId && roleUser.getRole().getId() === roleId
        ),
        1
      );
    }
  }


  clone(): User {
    const nextEditedUser = new User(
      this!.getId(),
      this!.getName(),
      this!.getFirstName(),
      this!.getMail(),
      this!.getEntityId(),
      this!.getSystem(),
      this!.getCreatedAt(),
      this!.getUpdatedAt()
    );
    // Re-copier les rôles explicitement si besoin
    (nextEditedUser as any).role_user = this!.getRoleUser();

    return nextEditedUser;
  }

}

export default User;